import React, {useState, useEffect} from "react"
import moment from "moment"
import RecurrenceField from "./RecurrenceField"
import BudgetCategoryPicker from "./BudgetCategoryPicker"
import {useFinanceData} from "../hooks/useFinanceData"
import NotificationPreference from "./NotificationPreference"
import ImprovedEmojiPicker from "./ImprovedEmojiPicker"

// Web-specific date picker component
const DatePicker = ({selectedDate, onChange, minDate}) => {
    return (
        <input
            type='date'
            value={selectedDate.format("YYYY-MM-DD")}
            min={minDate.format("YYYY-MM-DD")}
            onChange={(e) => {
                if (e.target.value) {
                    const newDate = moment(e.target.value)
                    onChange(newDate)
                }
            }}
            style={{
                padding: "0.5rem",
                borderRadius: "10px",
                border: "1px solid #e5e7eb",
                width: "100%",
            }}
        />
    )
}

const EditExpenseForm = ({
    onSubmit,
    onCancel,
    onDelete,
    initialValues,
    useDirectUpdate = false,
}) => {
    // Use the useFinanceData hook to access budgets, editItem, and deleteItem
    const {getBudgetCategories, editItem, addItemWithConflictCheck, deleteItem} =
        useFinanceData()
    const [amountInputValue, setAmountInputValue] = useState("")

    const [expense, setExpense] = useState({
        title: "",
        amount: 0,
        date: moment(),
        recurrenceFrequency: null,
        recurrencePeriod: "none",
        emoji: "💸",
        color: "#FF6347", // defaultTheme.expensesColor equivalent
        budgetCategoryId: null,
        notificationPreference: ["off"],
        createdAt: moment().toISOString(),
    })

    // Get all budgets from our hook
    const allBudgets = getBudgetCategories()

    useEffect(() => {
      if (initialValues) {
          // Convert notification preferences from object to array format
          let notificationArray = ["off"]
          if (initialValues.notificationPreference) {
              notificationArray = Object.entries(
                  initialValues.notificationPreference
              )
                  .map(([_, value]) => value)
                  .filter((value) => value !== "off")
              if (notificationArray.length === 0) {
                  notificationArray = ["off"]
              }
          }
  
          // Handle recurrence fields
          const recurrenceFrequency =
              initialValues.recurrenceFrequency === 0 ||
              initialValues.recurrenceFrequency === "none" ||
              !initialValues.recurrenceFrequency
                  ? null
                  : initialValues.recurrenceFrequency
  
          const recurrencePeriod =
              !recurrenceFrequency ||
              initialValues.recurrencePeriod === "none" ||
              !initialValues.recurrencePeriod
                  ? "none"
                  : initialValues.recurrencePeriod
  
          // Get the amount value
          const amount = initialValues.amount || 0
  
          // Initialize the amount input value string
          setAmountInputValue(Math.abs(amount).toString())
  
          // Ensure all required fields are explicitly set from initialValues
          const initialExpense = {
              ...expense, // Start with default state
              ...initialValues, // Overlay with initialValues
              title: initialValues.title || "", // Ensure title is present (defaults to empty string)
              date: moment(initialValues.date),
              amount: amount,
              recurrenceFrequency,
              recurrencePeriod,
              notificationPreference: notificationArray,
              budgetCategoryId: initialValues.budgetCategoryId || null,
              emoji: initialValues.emoji || "💸",
              color: initialValues.color || "#FF6347",
          }
  
          console.log("Setting initial expense state:", {
              title: initialExpense.title,
              budgetCategoryId: initialExpense.budgetCategoryId,
              emoji: initialExpense.emoji,
              recurrenceFrequency: initialExpense.recurrenceFrequency,
              recurrencePeriod: initialExpense.recurrencePeriod,
              amount: initialExpense.amount,
              notificationPreference: initialExpense.notificationPreference,
              date: initialExpense.date.format("YYYY-MM-DD"),
              color: initialExpense.color
          })
  
          setExpense(initialExpense)
      }
  }, [])

    const handleNotificationChange = (value) => {
        handleChange("notificationPreference", value)
        console.log("Updated notification preferences:", value)
    }

    const handleChange = (name, value) => {
        setExpense((prev) => ({...prev, [name]: value}))
    }

    // Handle amount change with validation for positive whole numbers
    const handleAmountChange = (e) => {
        const inputValue = e.target.value

        // Only allow digits (positive whole numbers) or empty string
        if (/^\d*$/.test(inputValue)) {
            // Update our string state for the input display
            setAmountInputValue(inputValue)

            // Update the actual amount value in the expense object
            // For the expense object state, we need a number (0 if empty)
            const numValue = inputValue === "" ? 0 : parseInt(inputValue, 10)
            handleChange("amount", numValue)
        }
    }

    // Get placeholder text based on budget category
    const getTitlePlaceholder = () => {
        if (!expense.budgetCategoryId) return "e.g. Some expense"

        const budget = allBudgets.find((b) => b.id === expense.budgetCategoryId)
        if (!budget) return "e.g. Some expense"

        // Category-specific placeholders
        const placeholders = {
            housing: "e.g., Rent, Mortgage, Property Tax",
            utilities: "e.g., Electricity, Water, Internet",
            food: "e.g., Groceries, Takeout, Coffee Shop",
            transportation: "e.g., Gas, Car Payment, Public Transit",
            entertainment: "e.g., Movies, Concerts, Streaming",
            personal: "e.g., Haircut, Gym, Subscription",
        }

        const normalizedCategory = budget.title
            .toLowerCase()
            .replace(/\s+/g, "")

        for (const [key, value] of Object.entries(placeholders)) {
            if (normalizedCategory.includes(key)) {
                return value
            }
        }

        return `e.g., ${budget.title} expense`
    }

    // Check functions for determining update type
    const isOnlyNotificationPreferenceChanged = (
        currentExpense,
        initialExpense
    ) => {
        if (!initialExpense) return false

        // Convert both notification arrays to sets for easier comparison
        const currentNotifications = new Set(
            currentExpense.notificationPreference
        )
        const initialNotifications = new Set(
            Array.isArray(initialExpense.notificationPreference)
                ? initialExpense.notificationPreference
                : Object.values(initialExpense.notificationPreference)
        )

        // First check if notifications are actually different
        const areNotificationsDifferent =
            currentNotifications.size !== initialNotifications.size ||
            ![...currentNotifications].every((value) =>
                initialNotifications.has(value)
            )

        // If notifications aren't different, it's not a notification-only change
        if (!areNotificationsDifferent) return false

        // Check all other relevant fields to ensure they're the same
        const fieldsToCompare = [
            "title",
            "amount",
            "recurrenceFrequency",
            "recurrencePeriod",
            "emoji",
            "color",
            "budgetCategoryId",
        ]

        // Check if date is the same (need to compare the actual date strings)
        const isSameDate = moment(currentExpense.date).isSame(
            moment(initialExpense.date)
        )

        // Check if all other fields are the same
        const areOtherFieldsSame = fieldsToCompare.every(
            (field) => currentExpense[field] === initialExpense[field]
        )

        return areNotificationsDifferent && isSameDate && areOtherFieldsSame
    }

    const isOnlyBasicPropertiesChanged = (currentExpense, initialExpense) => {
        if (!initialExpense) return false

        // Convert notification arrays to sets for comparison
        const currentNotifications = new Set(
            currentExpense.notificationPreference
        )
        const initialNotifications = new Set(
            Array.isArray(initialExpense.notificationPreference)
                ? initialExpense.notificationPreference
                : Object.values(initialExpense.notificationPreference)
        )

        // Check if any basic properties are different
        const hasBasicChanges =
            currentExpense.budgetCategoryId !==
                initialExpense.budgetCategoryId ||
            currentExpense.emoji !== initialExpense.emoji ||
            currentNotifications.size !== initialNotifications.size ||
            ![...currentNotifications].every((value) =>
                initialNotifications.has(value)
            )

        if (!hasBasicChanges) return false

        // Check if all other fields remain the same
        const fieldsToCompare = [
            "title",
            "amount",
            "recurrenceFrequency",
            "recurrencePeriod",
            "color",
        ]

        // Check if date is the same
        const isSameDate = moment(currentExpense.date).isSame(
            moment(initialExpense.date)
        )

        // Check if all other fields are the same
        const areOtherFieldsSame = fieldsToCompare.every(
            (field) => currentExpense[field] === initialExpense[field]
        )

        return hasBasicChanges && isSameDate && areOtherFieldsSame
    }

   // Update the isOnlyDateChanged function to better detect date changes
const isOnlyDateChanged = (currentExpense, initialExpense) => {
  if (!initialExpense) return false

  // Convert dates to strings for comparison
  const currentDate = moment(currentExpense.date).format("YYYY-MM-DD")
  const initialDate = moment(initialExpense.date).format("YYYY-MM-DD")

  // Check if dates are different
  if (currentDate === initialDate) return false

  // Check if all other relevant fields are the same
  const fieldsToCompare = [
      "title",
      "amount",
      "recurrenceFrequency",
      "recurrencePeriod",
      "emoji",
      "color",
      "budgetCategoryId",
      "notificationPreference",
  ]

  return fieldsToCompare.every((field) => {
      if (Array.isArray(currentExpense[field])) {
          return (
              JSON.stringify(currentExpense[field]) ===
              JSON.stringify(initialExpense[field])
          )
      }
      return currentExpense[field] === initialExpense[field]
  })
}

const handleSubmit = async () => {
  if (moment(expense.date).isBefore(moment(), "day")) {
      alert("Invalid Date: Please select a date that is not in the past.")
      return
  }

  console.log(
      "Starting handleSubmit with expense state:",
      JSON.stringify(expense, null, 2)
  )
  
  // Detailed expense item logging
  console.log("EXPENSE UPDATE - Details:")
  console.log("Title:", expense.title)
  console.log("Amount:", expense.amount)
  console.log("Date:", expense.date.format("YYYY-MM-DD"))
  console.log("Budget Category ID:", expense.budgetCategoryId)
  console.log("Emoji:", expense.emoji)
  console.log("Recurrence Frequency:", expense.recurrenceFrequency)
  console.log("Recurrence Period:", expense.recurrencePeriod)
  console.log("Notification Preference:", expense.notificationPreference)
  console.log("Is this occurrence recurring?", expense.recurrencePeriod !== "none" && expense.recurrenceFrequency)

  // Basic validation
  if (!expense.title || (expense.amount*-1) < 0) {
      console.log("Validation failed: Missing title or invalid amount")
      alert("Invalid Input: Please enter a title and a valid amount.")
      return
  }

  // Validate budget category
  if (!expense.budgetCategoryId) {
      console.log("Validation failed: Missing budget category")
      alert("Please select a budget category for this expense.")
      return
  }

  try {
      // Prepare the expense data
      const expenseData = {
          ...expense,
          type: "expense",
          amount: (expense.amount*-1),
          date: expense.date.format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
          notificationPreference: expense.notificationPreference,
          budgetCategoryId: expense.budgetCategoryId,
          emoji: expense.emoji,
      }

      console.log(
          "Prepared expense data:",
          JSON.stringify(expenseData, null, 2)
      )

      // For existing expenses
      if (initialValues) {
          // Check if only notification preference changed
          const isNotificationOnlyUpdate =
              isOnlyNotificationPreferenceChanged(expense, initialValues)
          console.log(
              "Is notification-only update?",
              isNotificationOnlyUpdate
          )

          if (isNotificationOnlyUpdate) {
              console.log("Handling notification-only update")
              const updatedExpense = {
                  ...initialValues,
                  notificationPreference: expense.notificationPreference,
              }

              if (useDirectUpdate) {
                  const result = await editItem(
                      updatedExpense,
                      "notification-only"
                  )
                  onSubmit(result, "notification-only")
              } else {
                  onSubmit(updatedExpense, "notification-only")
              }
              return
          }

          // Check if only basic properties changed (budget category or emoji)
          const isBasicUpdate = isOnlyBasicPropertiesChanged(
              expense,
              initialValues
          )
          console.log("Is basic update (category/emoji)?", isBasicUpdate)

          if (isBasicUpdate) {
              console.log("Handling basic properties update")

              if (useDirectUpdate) {
                  const result = await editItem(expenseData, "all")
                  onSubmit(result, "all")
              } else {
                  onSubmit(expenseData, "all")
              }
              return
          }

          // Check if only the date changed
          const isDateOnlyChange = isOnlyDateChanged(
              expense,
              initialValues
          )
          console.log("Is date-only change?", isDateOnlyChange)

          // Handle date changes for recurring expenses
          if (isDateOnlyChange && initialValues.recurrenceFrequency !== "none") {
              console.log("Handling date change for recurring expense")
              
              // Using confirm for options
              const updateChoice = window.confirm(
                  "How would you like to update this date?\n\n" +
                  "OK: Update this occurrence only\n" +
                  "Cancel: Show more options"
              )

              if (updateChoice) {
                  // User clicked OK - update single occurrence
                  console.log("Updating single occurrence date")
                  const occurrenceData = {
                      ...expenseData,
                      occurrenceDate: initialValues.date, // Original date of the occurrence
                  }

                  if (useDirectUpdate) {
                      const result = await editItem(occurrenceData, "single")
                      onSubmit(result, "single")
                  } else {
                      onSubmit(occurrenceData, "single")
                  }
              } else {
                  // User clicked Cancel - show more options
                  const updateFuture = window.confirm(
                      "Would you like to update all future occurrences to follow this new day pattern?\n\n" +
                      "OK: Update all future occurrences to follow this new pattern\n" +
                      "Cancel: Update all occurrences (past and future) to follow this new pattern"
                  )

                  if (updateFuture) {
                      console.log("Updating future occurrences with new date pattern")
                      // Include the date change and original occurrence date
                      const futureData = {
                          ...expenseData,
                          occurrenceDate: initialValues.date, // Original date being modified
                          datePatternChange: true // Flag to indicate date pattern change
                      }
                      
                      if (useDirectUpdate) {
                          const result = await editItem(futureData, "future")
                          onSubmit(result, "future")
                      } else {
                          onSubmit(futureData, "future")
                      }
                  } else {
                      console.log("Updating all occurrences with new date pattern")
                      // Include the date change flag
                      const allData = {
                          ...expenseData,
                          datePatternChange: true // Flag to indicate date pattern change
                      }
                      
                      if (useDirectUpdate) {
                          const result = await editItem(allData, "all")
                          onSubmit(result, "all")
                      } else {
                          onSubmit(allData, "all")
                      }
                  }
              }
              return
          }

          // Check if recurrence type is changing
          const isChangingToNonRecurring =
              initialValues.recurrenceFrequency !== "none" &&
              expense.recurrencePeriod === "none"
          const isChangingToRecurring =
              (initialValues.recurrenceFrequency === "none" ||
                  !initialValues.recurrenceFrequency) &&
              expense.recurrencePeriod !== "none"

          console.log(
              "Is changing to non-recurring?",
              isChangingToNonRecurring
          )
          console.log("Is changing to recurring?", isChangingToRecurring)

          if (isChangingToNonRecurring || isChangingToRecurring) {
              console.log(
                  "Converting expense recurrence type - updating all occurrences"
              )

              if (useDirectUpdate) {
                  const result = await editItem(expenseData, "all")
                  onSubmit(result, "all")
              } else {
                  onSubmit(expenseData, "all")
              }
              return
          }

          // Handle regular updates for recurring expenses
          if (initialValues.recurrenceFrequency !== "none") {
              // Using alert for options as it's web-equivalent of Alert.alert in React Native
              const updateSingle = window.confirm(
                  "How would you like to update this expense?\n\n" +
                      "OK: Update this occurrence only\n" +
                      "Cancel: Show more options"
              )

              if (updateSingle) {
                  // User clicked OK - update single occurrence
                  console.log("Updating single occurrence")
                  const occurrenceData = {
                      ...expenseData,
                      occurrenceDate: initialValues.date,
                  }

                  if (useDirectUpdate) {
                      const result = await editItem(
                          occurrenceData,
                          "single"
                      )
                      onSubmit(result, "single")
                  } else {
                      onSubmit(occurrenceData, "single")
                  }
              } else {
                  // User clicked Cancel - show more options
                  const updateFuture = window.confirm(
                      "Would you like to update all future occurrences?\n\n" +
                          "OK: Update all future occurrences\n" +
                          "Cancel: Update all occurrences (past and future)"
                  )

                  if (updateFuture) {
                      console.log("Updating future occurrences")
                      if (useDirectUpdate) {
                          const result = await editItem(
                              expenseData,
                              "future"
                          )
                          onSubmit(result, "future")
                      } else {
                          onSubmit(expenseData, "future")
                      }
                  } else {
                      console.log("Updating all occurrences")
                      if (useDirectUpdate) {
                          const result = await editItem(
                              expenseData,
                              "all"
                          )
                          onSubmit(result, "all")
                      } else {
                          onSubmit(expenseData, "all")
                      }
                  }
              }
          } else {
              // Non-recurring expense update
              console.log("Updating non-recurring expense")
              if (useDirectUpdate) {
                  const result = await editItem(expenseData, "all")
                  onSubmit(result, "all")
              } else {
                  onSubmit(expenseData, "all")
              }
          }
      } else {
          // New expense
          console.log("Creating new expense")
          if (useDirectUpdate) {
              const result = await addItemWithConflictCheck(expenseData)
              onSubmit(result)
          } else {
              onSubmit(expenseData)
          }
      }
  } catch (error) {
      console.error("Error in handleSubmit:", error)
      alert("Error: Failed to save expense. Please try again.")
  }
}

// Updated handleDelete function to use deleteItem from useFinanceData
const handleDelete = async () => {
    // For non-recurring expenses, show simple confirmation
    if (expense.recurrencePeriod === "none") {
        if (window.confirm("Are you sure you want to delete this expense?")) {
            console.log("Deleting non-recurring expense:", expense.id)
            
            if (useDirectUpdate) {
                try {
                    // Ensure the item has a type property explicitly set
                    const itemToDelete = {
                        ...expense,
                        type: "expense" // Explicitly set the type
                    };
                    
                    await deleteItem(itemToDelete, 'all')
                    onDelete(expense)
                } catch (error) {
                    console.error("Error deleting expense:", error)
                    alert("Error: Failed to delete expense. Please try again.")
                }
            } else {
                onDelete(expense)
            }
        }
        return
    }

    // For recurring expenses, show options
    const deleteType = window.confirm(
        "How would you like to delete this expense?\n\n" +
        "OK: Delete this occurrence only\n" +
        "Cancel: Show more options"
    )

    if (deleteType) {
        // User clicked OK - delete single occurrence
        console.log("Deleting single occurrence with date:", expense.date.format("YYYY-MM-DD"))
        
        if (useDirectUpdate) {
            try {
                // Ensure the item has a type property explicitly set
                const itemToDelete = {
                    ...expense,
                    type: "expense" // Explicitly set the type
                };
                
                await deleteItem(itemToDelete, 'single', expense.date)
                onDelete(expense, 'single', expense.date)
            } catch (error) {
                console.error("Error deleting single occurrence:", error)
                alert("Error: Failed to delete occurrence. Please try again.")
            }
        } else {
            onDelete(expense, 'single', expense.date)
        }
    } else {
        // User clicked Cancel - show more options
        const moreOptions = window.confirm(
            "Would you like to delete all future occurrences?\n\n" +
            "OK: Delete this & all future occurrences\n" +
            "Cancel: Delete all occurrences (past and future)"
        )

        if (moreOptions) {
            console.log("Deleting future occurrences from:", expense.date.format("YYYY-MM-DD"))
            
            if (useDirectUpdate) {
                try {
                    // Ensure the item has a type property explicitly set
                    const itemToDelete = {
                        ...expense,
                        type: "expense" // Explicitly set the type
                    };
                    
                    await deleteItem(itemToDelete, 'future', expense.date)
                    onDelete(expense, 'future', expense.date)
                } catch (error) {
                    console.error("Error deleting future occurrences:", error)
                    alert("Error: Failed to delete future occurrences. Please try again.")
                }
            } else {
                onDelete(expense, 'future', expense.date)
            }
        } else {
            console.log("Deleting all occurrences")
            
            if (useDirectUpdate) {
                try {
                    // Ensure the item has a type property explicitly set
                    const itemToDelete = {
                        ...expense,
                        type: "expense" // Explicitly set the type
                    };
                    
                    await deleteItem(itemToDelete, 'all')
                    onDelete(expense, 'all')
                } catch (error) {
                    console.error("Error deleting all occurrences:", error)
                    alert("Error: Failed to delete all occurrences. Please try again.")
                }
            } else {
                onDelete(expense, 'all')
            }
        }
    }
}

    const getMinDate = () => {
        return moment().startOf("day")
    }

    return (
        <div className='expense-form-container' style={styles.container}>
            <h2 style={styles.title}>EDIT EXPENSE</h2>

            <div style={styles.inputGroup}>
                <label style={styles.label}>Title</label>
                <input
                    type='text'
                    style={styles.input}
                    placeholder={getTitlePlaceholder()}
                    value={expense.title}
                    onChange={(e) => handleChange("title", e.target.value)}
                />
            </div>

            <div style={styles.inputGroup}>
                <label style={styles.label}>Budget Category</label>
                <div style={styles.inputWrapper}>
                    <BudgetCategoryPicker
                        selectedCategoryId={expense.budgetCategoryId}
                        onCategoryChange={(value) =>
                            handleChange("budgetCategoryId", value)
                        }
                        budgetItems={allBudgets}
                        disabled={false}
                    />
                </div>
            </div>

            <div style={styles.inputGroup}>
                <label style={styles.label}>Icon</label>
                <div style={styles.inputWrapper}>
                    <ImprovedEmojiPicker
                        value={expense.emoji || "💸"}
                        onChange={(value) => handleChange("emoji", value)}
                    />
                </div>
            </div>

            <div style={styles.inputGroup}>
                <label style={styles.label}>Amount</label>
                <div style={styles.currencyInputContainer}>
                    <span style={styles.currencySymbol}>$</span>
                    <input
                        type='text'
                        style={styles.input}
                        value={amountInputValue}
                        onChange={handleAmountChange}
                        placeholder='0'
                    />
                </div>
            </div>

            <div style={styles.inputGroup}>
                <label style={styles.label}>Due Date</label>
                <div style={styles.inputWrapper}>
                    <DatePicker
                        selectedDate={expense.date}
                        onChange={(date) => handleChange("date", date)}
                        minDate={getMinDate()}
                    />
                </div>
            </div>

            <div style={styles.inputGroup}>
                <label style={styles.label}>Notifications</label>
                <div style={styles.inputWrapper}>
                    <NotificationPreference
                        value={expense.notificationPreference}
                        onChange={handleNotificationChange}
                    />
                </div>
            </div>

            <div style={styles.recurrenceContainer}>
                <RecurrenceField
                    frequency={expense.recurrenceFrequency}
                    period={expense.recurrencePeriod}
                    onFrequencyChange={(value) =>
                        handleChange("recurrenceFrequency", value)
                    }
                    onPeriodChange={(value) =>
                        handleChange("recurrencePeriod", value)
                    }
                />
            </div>

            <div style={styles.buttonContainer}>
                <button style={styles.saveButton} onClick={handleSubmit}>
                    {initialValues ? "Update Expense" : "Save Expense"}
                </button>
                <button style={styles.cancelButton} onClick={onCancel}>
                    Cancel
                </button>
            </div>

            {initialValues && (
                <button style={styles.deleteButton} onClick={handleDelete}>
                    Delete
                </button>
            )}
        </div>
    )
}

// Inline styles
const styles = {
    container: {
        maxWidth: "600px",
        margin: "0 auto",
        padding: "20px",
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    },
    title: {
        fontSize: "18px",
        fontWeight: "bold",
        textAlign: "center",
        marginBottom: "20px",
        letterSpacing: "1.1px",
    },
    inputGroup: {
        display: "flex",
        alignItems: "center",
        marginBottom: "16px",
    },
    label: {
        flex: "1",
        fontWeight: "bold",
        fontSize: "14px",
    },
    inputWrapper: {
        flex: "2",
    },
    input: {
        flex: "2",
        padding: "10px",
        borderRadius: "10px",
        border: "1px solid #e5e7eb",
        fontSize: "14px",
        width: "100%",
    },
    currencyInputContainer: {
        display: "flex",
        alignItems: "center",
        flex: "2",
    },
    currencySymbol: {
        marginRight: "8px",
        fontSize: "16px",
    },
    recurrenceContainer: {
        marginBottom: "16px",
    },
    buttonContainer: {
        display: "flex",
        justifyContent: "space-between",
        marginTop: "24px",
        gap: "12px",
    },
    saveButton: {
        flex: "1",
        padding: "12px",
        backgroundColor: "#333333",
        color: "#ffffff",
        border: "none",
        borderRadius: "8px",
        cursor: "pointer",
        fontWeight: "bold",
    },
    cancelButton: {
        flex: "1",
        padding: "12px",
        backgroundColor: "#e5e7eb",
        color: "#333333",
        border: "none",
        borderRadius: "8px",
        cursor: "pointer",
        fontWeight: "bold",
    },
    deleteButton: {
        backgroundColor: "transparent",
        color: "#FF6347",
        border: "none",
        marginTop: "16px",
        cursor: "pointer",
        padding: "8px",
        display: "block",
        margin: "20px auto 0",
        fontWeight: "bold",
    },
}

export default EditExpenseForm